/**
 * EditAttendeeFeesDialog — iter197.5.
 *
 * Admin dialog to tweak individual attendee session-fees AFTER they were
 * bulk-charged. Each row has the current amount + a pencil → opens an
 * inline editor with [new amount] + [optional reason]. Submit hits
 * POST /api/sessions/{id}/charge-fee/edit-attendee which atomically
 * adjusts coin_balances + fund_balance.
 *
 * Triggered from the SessionCard kebab menu ("Edit per-attendee fee") or
 * the session detail page's AdminRecoveryCard.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Save, Edit3, CheckCircle2, X, Coins, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

export default function EditAttendeeFeesDialog({ open, sessionId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);  // user_id being edited
  const [draftAmount, setDraftAmount] = useState('');
  const [draftReason, setDraftReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/sessions/${sessionId}/charge-fee/attendees`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load fee breakdown');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open) load();
    else { setData(null); setEditing(null); }
  }, [open, load]);

  const onStartEdit = (row) => {
    setEditing(row.user_id);
    setDraftAmount(String(row.amount));
    setDraftReason('');
  };

  const onCancelEdit = () => {
    setEditing(null);
    setDraftAmount('');
    setDraftReason('');
  };

  const onSave = useCallback(async () => {
    const amt = parseFloat(draftAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error('Enter a valid amount (₹0 to waive)');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/charge-fee/edit-attendee`, {
        user_id: editing,
        new_amount: amt,
        reason: draftReason.trim() || undefined,
      });
      toast.success(r.data?.message || 'Updated');
      await load();
      setEditing(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Edit failed');
    } finally {
      setSaving(false);
    }
  }, [draftAmount, draftReason, editing, sessionId, load, onChange]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-attendee-fees-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-primary" />
            Edit per-attendee fees
          </DialogTitle>
          <DialogDescription>
            {data?.session_title}
            {data?.session_date ? ` · ${data.session_date}` : ''}
            {' · '}Total {data ? `₹${Math.round(data.fees_total).toLocaleString('en-IN')}` : '…'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        )}
        {error && (
          <div className="rounded-xl border-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        {data && !loading && data.attendees.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No session fees charged yet for this session.
          </div>
        )}
        {data && !loading && data.attendees.length > 0 && (
          <div className="space-y-1.5 py-2" data-testid="edit-attendee-fees-list">
            {data.attendees.map((row) => {
              const isEditing = editing === row.user_id;
              return (
                <div
                  key={row.txn_id}
                  className={`rounded-xl border-2 ${row.reversed ? 'border-border opacity-50' : 'border-border'} bg-card px-3 py-2`}
                  data-testid={`edit-attendee-row-${row.user_id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                      {(row.user_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{row.user_name}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {row.reversed ? (
                          <span className="text-red-600 font-semibold">reversed</span>
                        ) : row.edited ? (
                          <>edited from ₹{Math.round(row.edited_from_amount || 0)}{row.edited_reason ? ` · ${row.edited_reason}` : ''}</>
                        ) : (
                          'no edits'
                        )}
                      </p>
                    </div>
                    {!isEditing && (
                      <>
                        <span className="text-sm font-bold tabular-nums shrink-0">
                          ₹{Math.round(row.amount).toLocaleString('en-IN')}
                        </span>
                        {!row.reversed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onStartEdit(row)}
                            data-testid={`edit-attendee-edit-${row.user_id}`}
                            className="h-7 px-2 gap-1"
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  {isEditing && (
                    <div className="mt-2 pt-2 border-t border-border space-y-2" data-testid={`edit-attendee-editor-${row.user_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">₹</span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          value={draftAmount}
                          onChange={(e) => setDraftAmount(e.target.value)}
                          className="h-9 text-base flex-1"
                          autoFocus
                          data-testid={`edit-attendee-amount-${row.user_id}`}
                        />
                      </div>
                      <Input
                        placeholder="Reason (optional)"
                        value={draftReason}
                        onChange={(e) => setDraftReason(e.target.value)}
                        className="h-9 text-sm"
                        data-testid={`edit-attendee-reason-${row.user_id}`}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={onCancelEdit} disabled={saving} data-testid={`edit-attendee-cancel-${row.user_id}`} className="h-8 gap-1">
                          <X className="w-3.5 h-3.5" /> Cancel
                        </Button>
                        <Button size="sm" onClick={onSave} disabled={saving} data-testid={`edit-attendee-save-${row.user_id}`} className="h-8 gap-1">
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Save
                        </Button>
                      </div>
                      {parseFloat(draftAmount) !== row.amount && (
                        <p className="text-xs text-muted-foreground italic flex items-start gap-1">
                          {parseFloat(draftAmount) < row.amount
                            ? <><CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5" /> Refund of ₹{Math.round(row.amount - parseFloat(draftAmount))} will be credited to {row.user_name}.</>
                            : <><AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5" /> Additional ₹{Math.round(parseFloat(draftAmount) - row.amount)} will be charged to {row.user_name}.</>}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="edit-attendee-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
